import React, { useEffect, useRef, useState, type JSX } from "react";
import { ClipboardDocumentIcon, CheckIcon } from "@heroicons/react/24/outline";
import type { ConversationSummary } from "../types";
import { copyToClipboard } from "../llmRequestFormatting";
import { conversationShareText } from "./conversationShare";

const COPIED_MS = 2000;

/** Copies a conversation's id, user and deep link to the clipboard. */
export default function CopyConversationButton({
  conversation,
  showLabel = false,
}: {
  conversation: Pick<ConversationSummary, "conversation_id" | "username">;
  showLabel?: boolean;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.stopPropagation();
    const written = await copyToClipboard(
      conversationShareText(conversation, window.location.origin)
    );
    if (!written) return;
    setCopied(true);
    timer.current = setTimeout(() => setCopied(false), COPIED_MS);
  };

  const label = copied ? "Copied" : showLabel ? "Copy details" : null;

  return (
    <button
      type="button"
      aria-label="Copy conversation details"
      title="Copy the conversation ID, user and link"
      onClick={(e) => void handleClick(e)}
      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium text-sm"
    >
      {copied ? <CheckIcon className="h-4 w-4" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
      {label && <span>{label}</span>}
    </button>
  );
}
